import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { PaymentProvider } from '@/components/payment/PaymentProvider';
import { StripePaymentForm } from '@/components/payment/StripePaymentForm';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetDescription
} from '@/components/ui/sheet';

interface QuickCheckoutSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickCheckoutSheet({ open, onOpenChange }: QuickCheckoutSheetProps) {
  const { items, totalPrice, clearCart } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { hotelId } = useParams<{ hotelId: string }>();
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const handlePaymentSuccess = async (paymentId: string) => {
    sessionStorage.setItem('lastOrderId', '1');
    sessionStorage.setItem('lastOrderItems', JSON.stringify(items));

    clearCart();
    navigate(`/${hotelId}/confirmation`);
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-xl overflow-y-auto pb-safe">
        <SheetHeader className="text-left pb-4">
          <SheetTitle>{t('orderSummary')}</SheetTitle>
          <SheetDescription>
            {t('completeYourOrder')}
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6">
          <div className="border rounded-lg p-4">
            
            <div className="max-h-[30vh] overflow-y-auto space-y-3">
              {items.map((item, index) => (
                <div key={`${item.product.id}-${index}`}  className="flex items-center gap-3">
                <img src={item.product.image} alt={item.product.name} className="w-14 h-14 rounded-md object-cover" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{item.product.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('quantity')}: {item.quantity}
                  </p>
                </div>
                <div className="text-sm font-semibold">
                  €{(item.product.price * item.quantity).toFixed(2)}
                </div>
              </div>
              
              ))}
            </div>
          </div>
          
          <div className="space-y-4">            
            <PaymentProvider amount={totalPrice}>
              <StripePaymentForm 
                amount={totalPrice}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </PaymentProvider>
            
            {paymentError && (
              <p className="text-destructive text-sm">{paymentError}</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}